import axiosInterceptors from "./axiosHandler";
import { baseURL } from "./index";

// Car Data Bridge - Fixes missing car data connections
// This is a permanent fix for the disconnection between purchases and car data
const carDataBridge = {
  // Cache to store fixed car data
  fixedCarData: {},

  // Register a specific car that needs fixing
  registerFixedCar: function(carId, carData) {
    this.fixedCarData[carId] = carData;
  },

  // Get car data, using fixed data if available
  getFixedCarDetails: function(carId) {
    // Handle different carId formats
    let carIdStr;
    if (!carId) return null;
    
    if (typeof carId === 'object') {
      if (carId.$oid) carIdStr = carId.$oid;
      else if (carId._id) carIdStr = typeof carId._id === 'string' ? carId._id : carId._id.$oid || carId._id.toString();
      else if (carId.toString) carIdStr = carId.toString();
      else carIdStr = JSON.stringify(carId);
    } else {
      carIdStr = carId;
    }
    
    // Check if we have fixed data for this car
    if (this.fixedCarData[carIdStr]) {
      return this.fixedCarData[carIdStr];
    }
    
    return null;
  },

  // Fix a purchase by adding the correct car details
  fixPurchase: function(purchase) {
    if (!purchase) return purchase;
    
    // Car fixing logic removed as requested
    
    return purchase;
  },
  
  // Fix an array of purchases
  fixPurchases: function(purchases) {
    if (!Array.isArray(purchases)) return purchases;
    return purchases.map(purchase => this.fixPurchase(purchase));
  }
};

// Register the known fixed car data on module load
// Fixed car data removed as requested

// Fetch car details from both cars and oldcars collections
export const getCarDetails = async (carId) => {
  // Handle null or undefined carId
  if (!carId) return null;
  
  // Extract string ID from MongoDB ObjectId format if needed
  let idString;
  
  if (typeof carId === 'object') {
    // Handle MongoDB ObjectId format: { $oid: "..." }
    if (carId.$oid) {
      idString = carId.$oid;
    } 
    // Handle nested ObjectId: { _id: { $oid: "..." } }
    else if (carId._id && carId._id.$oid) {
      idString = carId._id.$oid;
    }
    // Handle direct _id property: { _id: "..." }
    else if (carId._id && typeof carId._id === 'string') {
      idString = carId._id;
    }
    // Fallback to toString
    else if (carId.toString) {
      idString = carId.toString();
    } 
    // Last resort
    else {
      idString = JSON.stringify(carId);
    }
  } else {
    // Already a string
    idString = carId;
  }

  try {
    console.log(`[DEBUG] getCarDetails: Fetching details for car ID: ${idString}`);
    
    // Create a fresh axios instance with current auth token
    const axiosInstance = axiosInterceptors();
    
    // Parallel requests to both endpoints with checkBothCollections flag
    const [carsResponse, oldCarsResponse] = await Promise.all([
      // Primary cars collection with flag to check both
      axiosInstance.post(baseURL + "/cars/detail", {
        id: idString,
        checkBothCollections: true
      }).catch(error => {
        console.log(`[DEBUG] getCarDetails: Error fetching from primary cars collection:`, error.message);
        return null;
      }),
      
      // Direct request to oldcars collection as backup
      axiosInstance.post(baseURL + "/cars/oldcars-detail", {
        id: idString
      }).catch(error => {
        console.log(`[DEBUG] getCarDetails: Error fetching from oldcars collection:`, error.message);
        return null;
      })
    ]);

    console.log(`[DEBUG] getCarDetails: Responses received - cars: ${!!carsResponse}, oldcars: ${!!oldCarsResponse}`);

    // Choose the response that has data, prioritizing oldcars (for historical purchases)
    let carData = null;
    
    // First check oldcars collection response
    if (oldCarsResponse && oldCarsResponse.data) {
      console.log(`[DEBUG] getCarDetails: Found car in oldcars collection`);
      carData = oldCarsResponse.data.data || oldCarsResponse.data;
    }
    // Then check primary cars collection response
    else if (carsResponse && carsResponse.data) {
      console.log(`[DEBUG] getCarDetails: Found car in primary cars collection`);
      carData = carsResponse.data.data || carsResponse.data;
    }

    // If we have car data, process and return it
    if (carData) {
      // Format car name for display
      const name = formatPurchaseCarName(carData);
      
      // Extract bid number
      const bidNo = carData.bidNo || 
                    (carData.bidDetail && carData.bidDetail.bidNo) || 
                    'N/A';
      
      // Return standardized car details object
      return {
        ...carData,
        name,
        bidNo,
        formattedId: idString
      };
    }
    
    console.log(`[DEBUG] getCarDetails: No car data found for ID: ${idString}`);
    return null;
  } catch (error) {
    console.error('Error fetching car details:', error);
    return null;
  }
};

// Fetch all purchases with car details
export const getAllPurchases = async () => {
  try {
    // Get purchases with checkBothCollections flag
    const response = await axiosInterceptors().get(baseURL + "/purchase/list", {
      params: {
        checkBothCollections: true
      }
    });

    if (!response || !response.data) {
      return { data: [], message: "No purchases found" };
    }

    // Process each purchase to include car details
    const purchasesWithDetails = await Promise.all(
      response.data.map(async (purchase) => {
        // Extract the carId to a standard format
        const formattedCarId = typeof purchase.carId === 'object' ?
          (purchase.carId.$oid || (purchase.carId._id && (purchase.carId._id.$oid || purchase.carId._id)) || JSON.stringify(purchase.carId)) :
          purchase.carId;

        // Fetch car details if we have a carId
        let carDetails = null;
        if (purchase.carId) {
          carDetails = await getCarDetails(purchase.carId);
        }

        // Return standardized purchase object with car details
        return {
          ...purchase,
          id: purchase._id,
          formattedCarId,
          name: carDetails?.name || 'N/A',
          bidNo: carDetails?.bidNo || 'N/A',
          carDetails
        };
      })
    );

    // Sort by createdAt, newest first
    const sortedPurchases = purchasesWithDetails.sort((a, b) => 
      new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );

    return { data: sortedPurchases, message: "Success" };
  } catch (error) {
    console.error('Error fetching purchases:', error);
    return { data: [], message: "Error fetching purchases" };
  }
};

// Fetch purchases for a specific user
export const getUserPurchases = async (userId) => {
  if (!userId) return { data: [], message: "No user ID provided" };
  
  try {
    // Get user purchases with checkBothCollections flag
    const response = await axiosInterceptors().get(baseURL + "/purchase", {
      params: {
        userId,
        checkBothCollections: true
      }
    });

    if (!response || !(response.data || []).length) {
      return { data: [], message: "No purchases found" };
    }

    const purchases = Array.isArray(response.data) ? response.data : 
                     (response.message === "Purchases Found" ? response.data : []);

    // Process each purchase to include car details (similar to getAllPurchases)
    const purchasesWithDetails = await Promise.all(
      purchases.map(async (purchase) => {
        // Extract the carId to a standard format
        const formattedCarId = typeof purchase.carId === 'object' ?
          (purchase.carId.$oid || (purchase.carId._id && (purchase.carId._id.$oid || purchase.carId._id)) || JSON.stringify(purchase.carId)) :
          purchase.carId;

        // Fetch car details if we have a carId
        let carDetails = null;
        if (purchase.carId) {
          carDetails = await getCarDetails(purchase.carId);
        }

        // Return standardized purchase object with car details
        return {
          ...purchase,
          id: purchase._id,
          formattedCarId,
          name: carDetails?.name || 'N/A',
          bidNo: carDetails?.bidNo || 'N/A',
          carDetails
        };
      })
    );

    // Sort by createdAt, newest first
    const sortedPurchases = purchasesWithDetails.sort((a, b) => 
      new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );

    return { data: sortedPurchases, message: "Success" };
  } catch (error) {
    console.error('Error fetching user purchases:', error);
    return { data: [], message: "Error fetching user purchases" };
  }
};

// Get car name with reliable fallbacks
export const formatPurchaseCarName = (content) => {
    if (!content) return 'N/A';
    
    try {
        // Get the year from model (model is the year in this data structure)
        const year = content.model || '';
        
        // Primary path: use makerDetail which has the complete information
        if (content?.makerDetail) {
            const maker = content.makerDetail?.maker || '';
            const model = content.makerDetail?.model || '';
            const grade = content.makerDetail?.grade || '';
            const type = content.makerDetail?.type || '';
            
            // Build name string with all available parts
            let carName = '';
            if (year) carName += `${year} `;
            if (maker) carName += `${maker} `;
            if (model) carName += `${model} `;
            if (grade) carName += `${grade} `;
            if (type) carName += type;
            
            return carName.trim() || 'N/A';
        }
        
        // Fallback to carDetail with vehicleDetailName if available
        if (content?.carDetail?.vehicleDetailName && Array.isArray(content.carDetail.vehicleDetailName)) {
            const detailArray = content.carDetail.vehicleDetailName;
            
            // Extract components from the array
            const maker = detailArray[0] || '';
            const model = detailArray[1] || '';
            const grade = detailArray[2] || '';
            
            // Try to find type from vehicleDetails
            let type = '';
            if (content?.carDetail?.vehicleDetails) {
                const typeDetail = content.carDetail.vehicleDetails.find(item => item.key === 'Type');
                if (typeDetail && typeDetail.value) {
                    type = typeDetail.value;
                }
            }
            
            // Build name string with all available parts
            let carName = '';
            if (year) carName += `${year} `;
            if (maker) carName += `${maker} `;
            if (model) carName += `${model} `;
            if (grade) carName += `${grade} `;
            if (type) carName += type;
            
            return carName.trim() || 'N/A';
        }
        
        // If we have vehicle information in other formats
        if (content?.vehicleType) {
            return content.vehicleType || 'N/A';
        }
        
        // If we have at least the model year
        if (content?.model) {
            return content.model || 'N/A';
        }
        
        return 'N/A';
    } catch (error) {
        console.error("Error processing car name for purchase:", error);
        return 'N/A';
    }
};

// Get user purchases with enhanced data processing and data bridge integration.
// Resolves to [] for a member with no purchases and rejects when the list
// could not be loaded, so My Garage can tell the two apart.
export const getUserPurchaseDetails = async (userId) => {
    try {
        // Add checkBothCollections flag to ensure backend checks both collections
        const response = await axiosInterceptors().get(baseURL + "/purchase", { 
            params: { 
                userId,
                checkBothCollections: true  // Add flag for backend
            } 
        });
        
        if (response?.message === "Purchases Found") {
            // Dump raw response data for debugging
            console.log("Raw purchase response data:", response.data.slice(0, 2));
            
            // Log car IDs from purchases to debug missing car details
            const carIds = response.data.map(p => ({
                purchaseId: p._id,
                carId: p.carId,
                hasCarDetails: !!p.carDetails,
                carDetailsSource: p.carDetails?.source || 'none'
            }));
            
            console.log("Purchase car IDs for debugging:", carIds);
            
            // Use car data bridge to fix purchases first
            const fixedPurchases = carDataBridge.fixPurchases(response.data);
            
            // Log the IDs of purchases for debugging purposes
            console.log("Purchase IDs being processed:", 
                fixedPurchases.map(p => ({ id: p._id, carId: p.carId, vin: p.vin }))
            );
            
            const purchasesWithCarDetails = await Promise.all(
                fixedPurchases.map(async (purchase) => {
                    try {
                        // Skip if carId is missing
                        if (!purchase.carId) {
                            console.log(`Purchase ${purchase._id} has no carId, using fallback data`);
                            const fallbackPurchase = {
                                ...purchase,
                                id: purchase._id,
                                carDetails: purchase.carDetails || {},
                                refId: purchase._id,
                                name: 'N/A',
                                auction: 'N/A',
                                bidNo: purchase.bidNo || 'N/A',
                                grade: 'N/A',
                                date: purchase.purchaseDate,
                                status: purchase.status,
                                createdAt: purchase.createdAt,
                                vin: purchase.vin || 'N/A'
                            };
                            
                            return fallbackPurchase;
                        }
                        
                        console.log(`Fetching details for purchase ${purchase._id} with carId:`, purchase.carId);
                        console.log(`CarId type: ${typeof purchase.carId}, value:`, 
                                    typeof purchase.carId === 'object' ? JSON.stringify(purchase.carId) : purchase.carId);
                        
                        // Check if we already have car details from the bridge
                        if (purchase.carDetails && Object.keys(purchase.carDetails).length > 0) {
                            console.log(`Using existing car details for purchase ${purchase._id}:`, 
                                { source: purchase.carDetails.source || 'unknown', hasData: true });
                                
                            const name = formatPurchaseCarName(purchase.carDetails);
                            
                            const processedPurchase = {
                                ...purchase,
                                id: purchase._id,
                                refId: purchase._id,
                                name,
                                auction: purchase.carDetails?.bidDetail?.bidSite || 'N/A',
                                bidNo: purchase.carDetails?.bidNo || purchase.carDetails?.bidDetail?.bidNo || purchase.bidNo || 'N/A',
                                grade: purchase.carDetails?.inspectionScore || 'N/A',
                                date: purchase.purchaseDate,
                                status: purchase.status,
                                createdAt: purchase.createdAt,
                                vin: purchase.vin || 'N/A'
                            };
                            
                            return processedPurchase;
                        }
                        
                        // Make a direct API call with checkBothCollections flag to ensure we get data from both collections
                        console.log(`Making direct API call for carId: ${typeof purchase.carId === 'object' ? JSON.stringify(purchase.carId) : purchase.carId}`);
                        const carDetails = await getCarDetails(purchase.carId);
                        console.log(`API call result for purchase ${purchase._id}:`, carDetails ? `Data received from ${carDetails.source || 'unknown source'}` : 'No data');
                        
                        if (carDetails) {
                            console.log(`Car details found for purchase ${purchase._id}:`, {
                                bidNo: carDetails.bidNo || carDetails.bidDetail?.bidNo,
                                makerExists: !!carDetails.makerDetail,
                                grade: carDetails.inspectionScore,
                                source: carDetails.source || 'unknown'
                            });
                            
                            const name = formatPurchaseCarName(carDetails);
                        
                            const processedPurchase = {
                                ...purchase,
                                id: purchase._id,
                                carDetails: carDetails || {},
                                refId: purchase._id,
                                name, // Formatted car name
                                auction: carDetails?.bidDetail?.bidSite || 'N/A', // Use bidSite as specified
                                bidNo: carDetails?.bidNo || carDetails?.bidDetail?.bidNo || 'N/A', // Use bidNo directly
                                grade: carDetails?.inspectionScore || 'N/A', // Use inspectionScore as specified
                                date: purchase.purchaseDate,
                                status: purchase.status,
                                createdAt: purchase.createdAt,
                                vin: purchase.vin || 'N/A'
                            };
                        
                            return processedPurchase;
                        } else {
                            console.log(`No car details found for purchase ${purchase._id}, trying special handling`);
                            
                            // Try explicitly checking oldcars collection
                            try {
                                console.log(`Trying explicit oldcars collection lookup for purchase ${purchase._id}`);
                                const oldCarsResponse = await axiosInterceptors().post(baseURL + "/cars/oldcars-detail", { id: purchase.carId });
                                
                                if (oldCarsResponse?.data) {
                                    console.log(`Found car data in oldcars collection for purchase ${purchase._id}`);
                                    const carData = oldCarsResponse.data?.data || oldCarsResponse.data;
                                    
                                    if (carData) {
                                        const name = formatPurchaseCarName(carData);
                                        
                                        const processedPurchase = {
                                            ...purchase,
                                            id: purchase._id,
                                            carDetails: carData,
                                            refId: purchase._id,
                                            name,
                                            auction: carData?.bidDetail?.bidSite || 'N/A',
                                            bidNo: carData?.bidNo || carData?.bidDetail?.bidNo || 'N/A',
                                            grade: carData?.inspectionScore || 'N/A',
                                            date: purchase.purchaseDate,
                                            status: purchase.status,
                                            createdAt: purchase.createdAt,
                                            vin: purchase.vin || 'N/A'
                                        };
                                        
                                        return processedPurchase;
                                    }
                                }
                            } catch (oldCarsError) {
                                console.error(`Error trying oldcars endpoint for purchase ${purchase._id}:`, oldCarsError);
                            }
                            
                            // Special handling for specific purchase IDs with known issues
                            if (purchase._id === "682d8734bcc58ce8d6a7bd1a" || 
                                purchase._id === "682d47887834a78f3c47bded" ||
                                purchase._id === "682d840ab5d8b7feefa1d2eb") {
                                
                                console.log(`Applying special fix for purchase ${purchase._id}`);
                                
                                // Make direct request to car details endpoint with both collection flag
                                const response = await axiosInterceptors().post(baseURL + "/cars/detail", { 
                                    id: purchase.carId,
                                    checkBothCollections: true  // Explicitly ask backend to check both collections
                                });
                                
                                const carData = response?.data?.data || response?.data;
                                console.log(`Special API call result:`, carData ? 'Data received' : 'No data');
                                
                                if (carData) {
                                    const name = formatPurchaseCarName(carData);
                                    
                                    const processedPurchase = {
                                        ...purchase,
                                        id: purchase._id,
                                        carDetails: carData,
                                        refId: purchase._id,
                                        name, 
                                        auction: carData?.bidDetail?.bidSite || 'N/A',
                                        bidNo: carData?.bidNo || carData?.bidDetail?.bidNo || 'N/A',
                                        grade: carData?.inspectionScore || 'N/A',
                                        date: purchase.purchaseDate,
                                        status: purchase.status,
                                        createdAt: purchase.createdAt,
                                        vin: purchase.vin || 'N/A'
                                    };
                                    
                                    return processedPurchase;
                                }
                            }
                            
                            // If all attempts failed, use fallback
                            const fallbackPurchase = {
                                ...purchase,
                                id: purchase._id,
                                carDetails: purchase.carDetails || {},
                                refId: purchase._id,
                                name: 'N/A',
                                auction: 'N/A',
                                bidNo: purchase.bidNo || 'N/A',
                                grade: 'N/A',
                                date: purchase.purchaseDate,
                                status: purchase.status,
                                createdAt: purchase.createdAt,
                                vin: purchase.vin || 'N/A'
                            };
                            
                            return fallbackPurchase;
                        }
                    } catch (error) {
                        console.error(`Error processing purchase ${purchase._id}:`, error);
                        
                        const errorPurchase = {
                            ...purchase,
                            id: purchase._id,
                            carDetails: purchase.carDetails || {},
                            refId: purchase._id,
                            name: 'N/A',
                            auction: 'N/A',
                            bidNo: purchase.bidNo || 'N/A',
                            grade: 'N/A',
                            date: purchase.purchaseDate,
                            status: purchase.status,
                            createdAt: purchase.createdAt,
                            vin: purchase.vin || 'N/A'
                        };
                        return errorPurchase;
                    }
                })
            );

            // Filter out null values and sort by createdAt timestamp (newest first)
            const resultPurchases = purchasesWithCarDetails
                .filter(purchase => purchase !== null)
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            return resultPurchases;
        }
        
        throw new Error(response?.message || 'Could not load purchases');
    } catch (error) {
        console.error('Error fetching user purchases:', error);
        throw error;
    }
};

// Admin function to get all purchases with data bridge integration
export const getAllPurchaseDetails = async () => {
    try {
        const response = await axiosInterceptors().get(baseURL + "/purchase/list", {
            params: {
                checkBothCollections: true // Add flag to ensure backend checks both collections
            }
        });
        
        if (response?.data) {
            const purchaseData = response.data || [];
            
            // Purchases should now have carData property with all car details
            // Format the data for consistency with the rest of the application
            const formattedPurchases = purchaseData.map(purchase => {
                const {
                    carData,
                    _id,
                    ...rest
                } = purchase;
                
                return {
                    ...rest,
                    _id,
                    id: _id,
                    name: carData ? formatPurchaseCarName(carData) : 'N/A',
                    bidNo: carData?.bidNo || carData?.bidDetail?.bidNo || 'N/A',
                    carDetails: carData,
                    carId: purchase.carId
                };
            });
            
            // Sort by createdAt, newest first
            const sortedPurchases = formattedPurchases.sort((a, b) => 
                new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
            );
            
            return { data: sortedPurchases, message: "Success" };
        }
        
        return { data: [], message: "No purchases found" };
    } catch (error) {
        console.error("Error fetching all purchases:", error);
        return { data: [], message: "Error fetching purchases" };
    }
};

// Function to update purchase details
export const updatePurchaseDetails = async (data, purchaseId, userId) => {
    console.log(`DEBUG - Purchase Service: Updating purchase details for purchaseId: ${purchaseId}, userId: ${userId}`);
    console.log(`DEBUG - Purchase Service: Update data:`, data);
    
    try {
        console.log(`DEBUG - Purchase Service: Making API call to update purchase`);
        const response = await axiosInterceptors().put(
            baseURL + "/purchase", 
            data, 
            { params: { purchaseId, userId } }
        );
        
        console.log(`DEBUG - Purchase Service: Update purchase response:`, response);
        return response;
    } catch (error) {
        console.error('DEBUG - Purchase Service: Error updating purchase details:', error);
        console.error('DEBUG - Purchase Service: Error details:', error.response || error.message);
        throw error;
    }
};

// Function to update purchase status
export const updatePurchaseStatus = async (data, purchaseId, userId) => {
    console.log(`DEBUG - Purchase Service: Updating purchase status for purchaseId: ${purchaseId}, userId: ${userId}`);
    console.log(`DEBUG - Purchase Service: Update status data:`, data);
    
    try {
        console.log(`DEBUG - Purchase Service: Making API call to update purchase status`);
        const response = await axiosInterceptors().post(
            baseURL + "/purchase/status", 
            data, 
            { params: { purchaseId, userId } }
        );
        
        console.log(`DEBUG - Purchase Service: Update status response:`, response);
        return response;
    } catch (error) {
        console.error('DEBUG - Purchase Service: Error updating purchase status:', error);
        console.error('DEBUG - Purchase Service: Error details:', error.response || error.message);
        throw error;
    }
};

// Create a new purchase
export const createPurchase = async (data) => {
    console.log(`DEBUG - Purchase Service: Creating new purchase with data:`, data);
    
    try {
        console.log(`DEBUG - Purchase Service: Making API call to create purchase`);
        const response = await axiosInterceptors().post(baseURL + "/purchase", data);
        console.log(`DEBUG - Purchase Service: Create purchase response:`, response);
        return response;
    } catch (error) {
        console.error('DEBUG - Purchase Service: Error creating purchase:', error);
        console.error('DEBUG - Purchase Service: Error details:', error.response || error.message);
        throw error;
    }
};

// Where the ship with this IMO is now, in the shape of the dealer garage's
// tracking answer - { message: 'Success', data: position | null, voyage } -
// so My Garage's ShipTracking panel reads both. Rejects when the lookup fails.
export const getTrackingDetails = async (imo) => {
    const response = await axiosInterceptors().get(baseURL + `/track/${encodeURIComponent(imo)}`);
    if (!Array.isArray(response?.data)) {
        throw new Error(response?.error || 'Tracking unavailable');
    }
    // The endpoint fills a missing name or destination with 'N/A'.
    const text = (value) => (value && value !== 'N/A' ? String(value) : '');
    const ship = response.data[0];
    const hasPosition = !!ship && ship.latitude != null && ship.longitude != null;
    return {
        message: 'Success',
        data: hasPosition
            ? {
                latitude: Number(ship.latitude),
                longitude: Number(ship.longitude),
                name: text(ship.name),
                destination: text(ship.destination),
                speed: ship.speed,
                eta: ship.eta,
                reportedAt: ship.reportedAt,
                fetchedAt: ship.fetchedAt,
            }
            : null,
        voyage: response.voyage || null,
    };
};

// Function to explain the connection between frontend and backend for car data
export const getCarFromBothCollectionsExplanation = () => {
  return {
    note: `
      The frontend doesn't directly call getCarFromBothCollections since it's a backend function.
      Instead, it relies on these backend API endpoints that internally use getCarFromBothCollections:
      
      1. /cars/detail - Used by getCarDetails with checkBothCollections: true flag
      2. /purchase - Used by getUserPurchaseDetails with checkBothCollections: true flag
      3. /purchase/list - Used by getAllPurchaseDetails with checkBothCollections: true flag
      4. /cars/oldcars-detail - Direct endpoint for oldcars collection queries
      
      The frontend code now implements multiple strategies to ensure car data is retrieved:
      - Always passing checkBothCollections: true to backend endpoints
      - Making parallel requests to increase chances of getting data
      - Directly querying oldcars collection via dedicated endpoints
      - Special handling for known problematic purchases
      
      This ensures the frontend can display car data regardless of whether it's in the 
      active 'cars' collection or the archived 'oldcars' collection.
    `
  };
};

// Export the car data bridge service for direct use in components if needed
export const carDataBridgeService = carDataBridge; 